import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Calendar, Clock, MapPin, ExternalLink, CalendarPlus, Sparkles } from "lucide-react";
import type { EventData } from "@/services/sanity/types";
import { buildGoogleCalendarUrl } from "@/services/calendar/events";
import { formatEventDateInfo } from "@/services/calendar/format-event";

interface EventModalProps {
    event: EventData | null;
    isOpen: boolean;
    onClose: () => void;
    t: (key: string) => string;
}

export const EventModal: React.FC<EventModalProps> = ({ event, isOpen, onClose, t }) => {
    // Keep last non-null event in state so exit animation can render while AnimatePresence unmounts
    const [lastEvent, setLastEvent] = useState<EventData | null>(event);

    if (event && event !== lastEvent) {
        setLastEvent(event);
    }

    const displayEvent = event ?? lastEvent;

    // Close on Escape key press
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    // Prevent body scrolling when modal is open
    useEffect(() => {
        if (!isOpen) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [isOpen]);

    const gcalUrl = displayEvent ? buildGoogleCalendarUrl(displayEvent) : "";
    const dateInfo = displayEvent ? formatEventDateInfo(displayEvent, t) : null;
    const modalBadge = displayEvent
        ? displayEvent.customTag ||
          (displayEvent.isHighlighted ? t("events.featuredEventBadge") || "Featured Event" : undefined)
        : undefined;

    return (
        <AnimatePresence>
            {isOpen && displayEvent && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="event-modal-title"
                >
                    {/* Backdrop overlay with exit animation */}
                    <motion.div
                        key="event-modal-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={onClose}
                        aria-hidden="true"
                    />

                    {/* Modal Container with scale and fade exit animation */}
                    <motion.div
                        key="event-modal-dialog"
                        initial={{ opacity: 0, scale: 0.95, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 16 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        className="relative w-full max-w-3xl max-h-[90vh] bg-white dark:bg-warm-dark-surface rounded-2xl shadow-2xl border border-warm-border dark:border-warm-dark-border overflow-hidden flex flex-col z-10 my-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label={t("events.closeModal") || "Close modal"}
                            className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-stone-900/60 hover:bg-stone-900/80 text-white flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            <X size={18} />
                        </button>

                        <div className="overflow-y-auto flex-1">
                            {/* Event Poster Image (if available) */}
                            {displayEvent.image && (
                                <div className="relative w-full bg-stone-950 flex items-center justify-center max-h-[380px] overflow-hidden group">
                                    <img
                                        src={displayEvent.image}
                                        alt={`${displayEvent.title} flyer`}
                                        className="w-full h-auto max-h-[380px] object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                                        loading="lazy"
                                    />
                                    {modalBadge && (
                                        <div className="absolute top-3 left-4">
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-primary text-white shadow-sm">
                                                <Sparkles size={12} className={displayEvent.isHighlighted ? "text-amber-300" : ""} />
                                                <span>{modalBadge}</span>
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Content */}
                            <div className="p-6 sm:p-8">
                                {!displayEvent.image && modalBadge && (
                                    <div className="flex flex-wrap items-center gap-2 mb-3">
                                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                                            <Sparkles size={14} className={displayEvent.isHighlighted ? "text-amber-500" : ""} />
                                            <span>{modalBadge}</span>
                                        </div>
                                    </div>
                                )}

                                <h2
                                    id="event-modal-title"
                                    className="font-serif text-2xl sm:text-3xl font-bold text-secondary dark:text-white mb-4 leading-tight"
                                >
                                    {displayEvent.title}
                                </h2>

                                {/* Details Grid */}
                                <div className="space-y-3 mb-6 text-stone-700 dark:text-stone-200">
                                    <div className="flex items-start gap-3">
                                        <Calendar className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                        <div>
                                            <div className="font-medium">{dateInfo?.formattedDate}</div>
                                            {dateInfo?.formattedTime && (
                                                <div className="text-sm text-stone-500 dark:text-stone-400 flex items-center gap-1 mt-0.5">
                                                    <Clock size={14} className="shrink-0" />
                                                    <span>{dateInfo.modalTimeDetails || dateInfo.formattedTime}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                        <div>
                                            <div className="font-medium">{displayEvent.location}</div>
                                            {displayEvent.address && displayEvent.address !== displayEvent.location && (
                                                <div className="text-sm text-stone-500 dark:text-stone-400">
                                                    {displayEvent.address}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Description */}
                                {displayEvent.description && (
                                    <div className="pt-4 border-t border-warm-border dark:border-warm-dark-border mb-6">
                                        <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-2">
                                            {t("events.aboutThisEvent") || "About this event"}
                                        </h4>
                                        <p className="text-stone-600 dark:text-stone-300 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                                            {displayEvent.description}
                                        </p>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="pt-4 border-t border-warm-border dark:border-warm-dark-border flex flex-col sm:flex-row gap-3">
                                    {displayEvent.googleMapsLink && (
                                        <a
                                            href={displayEvent.googleMapsLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-5 rounded-xl bg-primary text-white hover:bg-primary-hover font-medium text-sm transition-colors shadow-sm"
                                        >
                                            <MapPin size={16} />
                                            <span>{t("events.getDirections")}</span>
                                            <ExternalLink size={14} className="opacity-70" />
                                        </a>
                                    )}

                                    <a
                                        href={gcalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-5 rounded-xl border border-warm-border dark:border-warm-dark-border text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-warm-dark-bg font-medium text-sm transition-colors"
                                    >
                                        <CalendarPlus size={16} className="text-primary" />
                                        <span>{t("events.addToGoogleCalendar") || "Add to Google Calendar"}</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
